import styled, { css } from 'styled-components';
import tw, { theme } from 'twin.macro';
import Sticky from '../../../../styled components/Sticky';
import tailwindConfig from '../../../../../tailwind.config';
import { happeningNowDotRoom } from '../../ListViewBookings/ListViewBookings.style';

/* The table itself: the box it scrolls in, its header row and the body its
 * rows are in. The rows and the cells are in ListViewTableRow.style. */

/* The box the table is in, and the only thing on the page that scrolls it:
   sideways when its columns together ask for more room than the view has, and
   up and down within the height it is capped at. The two go together - a box
   that scrolls sideways is what its sticky header row sticks against, so it
   has to be the element that scrolls vertically as well, or the row would
   never stick at all.

   The cap is the height of the view less what stands around the box: the app
   header the page sits under, the toolbar above it and the paging below it.
   100dvh rather than 100vh so a mobile browser's collapsing chrome does not
   leave the box taller than the room there actually is; the plain vh line
   before it is what a browser without dvh falls back to. */
export const TableScrollBox = styled.div`
  overflow: auto;
  max-height: calc(100vh - ${tailwindConfig.extend.headerHeight / 16}rem - 11rem);
  max-height: calc(100dvh - ${tailwindConfig.extend.headerHeight / 16}rem - 11rem);
`;

/* The header row stays in view while the rows scroll under it: the shared
   Sticky primitive, rendered as the cell itself. What it sticks against is
   TableScrollBox, so it is pinned at the top of that box rather than under the
   app header.

   Its underline is a shadow and not a border: with border-collapse the table
   paints the borders rather than the cells, so a border would stay behind at
   the row's place in the table while the cell itself moves. */
export const TableHeaderCell = styled(Sticky).attrs(() => ({
  as: 'th'
}))<{ $minWidthInCharacters?: number; $indentedForHappeningNowDot?: boolean }>(
  ({ $minWidthInCharacters, $indentedForHappeningNowDot }) => [
    tw`text-left align-bottom break-words px-8 py-4 bg-white`,
    css`
      box-shadow: inset 0 -1px 0 ${theme`colors.silver.base`};
    `,
    /* The header of the column the dot marks: indented by the dot's room on
       top of the cell's own left padding, so the label starts where every
       value below it does rather than a dot's width to their left */
    $indentedForHappeningNowDot &&
      css`
        padding-left: calc(${theme`spacing.8`} + ${happeningNowDotRoom});
      `,
    /* A floor under the column's width, set on the header because that is the
     one cell every column has: the table gives a column no more than the room
     its widest cell asks for, so a column whose values are commonly a little
     wider than that wraps on nearly every row. In characters, the unit the
     values themselves are measured in - see minimumColumnWidthInCharacters.

     At every width, narrow views included: a column that cannot have its
     minimum there widens the table past the box instead of wrapping down to
     its longest word, and TableScrollBox scrolls to the rest of it. */
    $minWidthInCharacters !== undefined &&
      css`
        /* Plus the cell's own 8px of padding at either side: box-sizing is
           border-box here, so the padding would otherwise come out of the room
           the characters were asked for */
        min-width: calc(${$minWidthInCharacters}ch + 1rem);
      `
  ]
);

export const BookingRows = styled.tbody`
  & > tr:last-child > td {
    ${tw`border-b-0`}
  }
`;
